/**
 * ARCHIVO GENERADO — no editar.
 *
 * El único lugar del cliente que conoce una familia de íconos. Familia activa:
 * lucide. Se cambia en la configuración del proyecto, no acá.
 */

import {
  Activity,
  AlignLeft,
  ArchiveRestore,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  BadgeCheck,
  Ban,
  Bandage,
  Bell,
  BellPlus,
  BellRing,
  BookOpen,
  Bot,
  Boxes,
  Brain,
  Building,
  Building2,
  Calendar,
  CalendarDays,
  Camera,
  ChartColumn,
  ChartLine,
  ChartNoAxesCombined,
  ChartPie,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleCheckBig,
  CircleDollarSign,
  CircleDot,
  CircleMinus,
  CirclePause,
  CirclePlay,
  CircleQuestionMark,
  CircleStop,
  CircleX,
  ClipboardCheck,
  ClipboardClock,
  ClipboardList,
  Clock,
  CloudCheck,
  CloudOff,
  Coins,
  Columns3,
  Copy,
  CornerDownRight,
  CreditCard,
  Database,
  Dices,
  Disc3,
  DollarSign,
  Download,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  FastForward,
  FilePen,
  FilePlus,
  FileText,
  FileUp,
  Files,
  FlaskConical,
  FolderOpen,
  Gauge,
  Gavel,
  Globe,
  Grid3x3,
  GripVertical,
  Group,
  Hand,
  HardHat,
  Headset,
  History,
  Hourglass,
  House,
  IdCard,
  ImageOff,
  Info,
  KeyRound,
  Landmark,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Link,
  List,
  ListChecks,
  ListFilter,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Maximize,
  Maximize2,
  MemoryStick,
  Menu,
  MessageCircle,
  MessageSquareText,
  MessagesSquare,
  Mic,
  MicOff,
  Minimize,
  Monitor,
  Moon,
  Music,
  Network,
  OctagonAlert,
  Palette,
  PanelRight,
  PanelsTopLeft,
  Pause,
  PenTool,
  Pencil,
  Play,
  Plus,
  Power,
  Printer,
  Radar,
  RadioTower,
  Receipt,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  RotateCcwKey,
  Save,
  Scale,
  ScanText,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  Settings2,
  ShieldCheck,
  ShoppingCart,
  Sigma,
  SignalHigh,
  SlidersHorizontal,
  Square,
  SquareCheck,
  Star,
  Store,
  Sun,
  Table,
  Tags,
  ThumbsDown,
  ThumbsUp,
  TimerOff,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Upload,
  UploadCloud,
  User,
  Users,
  Webhook,
  Wifi,
  WifiOff,
  Wrench,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import * as React from "react";
import SvgIcon from "@mui/material/SvgIcon";

/**
 * Adapta un ícono de Lucide a la interfaz que espera el resto de la aplicación.
 *
 * Los de Lucide son SVG sueltos: no aceptan `sx`, ni `fontSize`, ni
 * `color="success"`, que es como los usan 388 lugares del cliente. Envolverlos
 * en el `SvgIcon` de MUI —que es el mecanismo previsto para íconos de
 * terceros— les devuelve esa interfaz entera en vez de obligar a reescribir
 * cada uso.
 *
 * El grosor del trazo NO se escribe acá: lo pone el tema, como override de
 * `MuiSvgIcon`. Estaba horneado en generación —una constante en este archivo—
 * y por eso el laboratorio podía moverlo sin que la pantalla cambiara: el valor
 * estaba congelado en el archivo emitido.
 *
 * `fill` y `stroke` NO son decorativos acá: `SvgIcon` pinta `fill:
 * currentColor` por CSS, y una regla CSS le gana al atributo `fill="none"`
 * que Lucide trae en el SVG. Sin estas dos lineas los iconos de trazo se
 * rellenan y se ven como manchas solidas en vez de como dibujos.
 */
const adaptar = (Lucide: any) => {
  const Adaptado = React.forwardRef<SVGSVGElement, any>((props, ref) => (
    <SvgIcon
      {...props}
      ref={ref}
      component={Lucide}
      inheritViewBox
      sx={{
        fill: "none",
        stroke: "currentColor",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        ...(props.sx ?? {}),
      }}
    />
  ));
  Adaptado.displayName = Lucide.displayName ?? "LucideIcon";
  return Adaptado;
};

/**
 * Marcador para un ícono que se pidió y no existe. Es visible y feo a
 * propósito: un ícono que falta y no se nota se descubre en producción.
 */
export const UnknownIcon = (props: any) =>
  React.createElement(
    "span",
    { ...props, title: "ícono desconocido", style: { opacity: 0.6 } },
    "?"
  );

export const AccountBalanceIcon = adaptar(Landmark);
export const AccountTreeIcon = adaptar(Network);
export const AddIcon = adaptar(Plus);
export const AlbumIcon = adaptar(Disc3);
export const ApiIcon = adaptar(Webhook);
export const ArrowBackIcon = adaptar(ArrowLeft);
export const ArrowForwardIcon = adaptar(ArrowRight);
export const ArrowForwardIosSharpIcon = adaptar(ChevronRight);
export const ArticleIcon = adaptar(FileText);
export const AssignmentIcon = adaptar(ClipboardList);
export const AttachMoneyIcon = adaptar(DollarSign);
export const AutorenewIcon = adaptar(RefreshCw);
export const BadgeIcon = adaptar(IdCard);
export const BadgeOutlinedIcon = adaptar(IdCard);
export const BalanceIcon = adaptar(Scale);
export const BarChartIcon = adaptar(ChartColumn);
export const BlockIcon = adaptar(Ban);
export const BuildIcon = adaptar(Wrench);
export const BusinessIcon = adaptar(Building2);
export const CancelIcon = adaptar(CircleX);
export const CasinoIcon = adaptar(Dices);
export const CategoryIcon = adaptar(Tags);
export const CellTowerIcon = adaptar(RadioTower);
export const ChatIcon = adaptar(MessageCircle);
export const CheckIcon = adaptar(Check);
export const CheckBoxIcon = adaptar(SquareCheck);
export const CheckCircleIcon = adaptar(CircleCheck);
export const CheckCircleOutlineIcon = adaptar(CircleCheck);
export const ChecklistIcon = adaptar(ListChecks);
export const ChevronLeftIcon = adaptar(ChevronLeft);
export const ChevronRightIcon = adaptar(ChevronRight);
export const CircleIcon = adaptar(Circle);
export const ClearIcon = adaptar(X);
export const CloseIcon = adaptar(X);
export const CloudDoneOutlinedIcon = adaptar(CloudCheck);
export const CloudOffIcon = adaptar(CloudOff);
export const CloudOffOutlinedIcon = adaptar(CloudOff);
export const CloudUploadIcon = adaptar(UploadCloud);
export const CollectionsBookmarkIcon = adaptar(Library);
export const CompareArrowsIcon = adaptar(ArrowLeftRight);
export const ContentCopyIcon = adaptar(Copy);
export const DangerousIcon = adaptar(OctagonAlert);
export const DarkModeIcon = adaptar(Moon);
export const DashboardIcon = adaptar(LayoutDashboard);
export const DeleteIcon = adaptar(Trash2);
export const DeleteForeverIcon = adaptar(Trash2);
export const DeleteOutlineIcon = adaptar(Trash2);
export const DescriptionIcon = adaptar(FileText);
export const DnsIcon = adaptar(Server);
export const DocumentScannerIcon = adaptar(ScanText);
export const DomainIcon = adaptar(Building);
export const DownloadIcon = adaptar(Download);
export const DragIndicatorIcon = adaptar(GripVertical);
export const DrawIcon = adaptar(PenTool);
export const EditIcon = adaptar(Pencil);
export const EditNoteIcon = adaptar(FilePen);
export const EngineeringIcon = adaptar(HardHat);
export const EngineeringOutlinedIcon = adaptar(HardHat);
export const ErrorIcon = adaptar(CircleAlert);
export const ErrorOutlineIcon = adaptar(CircleAlert);
export const EventIcon = adaptar(Calendar);
export const EventNoteIcon = adaptar(CalendarDays);
export const ExpandLessIcon = adaptar(ChevronUp);
export const ExpandMoreIcon = adaptar(ChevronDown);
export const FactCheckIcon = adaptar(ClipboardCheck);
export const FastForwardIcon = adaptar(FastForward);
export const FileCopyIcon = adaptar(Files);
export const FilterListIcon = adaptar(ListFilter);
export const FolderSharedIcon = adaptar(FolderOpen);
export const FormatAlignLeftIcon = adaptar(AlignLeft);
export const FullscreenIcon = adaptar(Maximize);
export const FullscreenExitIcon = adaptar(Minimize);
export const FunctionsIcon = adaptar(Sigma);
export const GavelIcon = adaptar(Gavel);
export const GetAppIcon = adaptar(Download);
export const GradeIcon = adaptar(Star);
export const GroupIcon = adaptar(Users);
export const GroupWorkIcon = adaptar(Boxes);
export const HealingIcon = adaptar(Bandage);
export const HelpOutlineIcon = adaptar(CircleQuestionMark);
export const HistoryIcon = adaptar(History);
export const HistoryEduIcon = adaptar(ScrollText);
export const HomeIcon = adaptar(House);
export const HourglassEmptyIcon = adaptar(Hourglass);
export const HubIcon = adaptar(Network);
export const ImageNotSupportedIcon = adaptar(ImageOff);
export const InfoIcon = adaptar(Info);
export const InsightsIcon = adaptar(ChartNoAxesCombined);
export const KeyboardArrowDownIcon = adaptar(ChevronDown);
export const KeyboardArrowRightIcon = adaptar(ChevronRight);
export const LayersIcon = adaptar(Layers);
export const LibraryBooksIcon = adaptar(Library);
export const LightModeIcon = adaptar(Sun);
export const LinkIcon = adaptar(Link);
export const ListAltIcon = adaptar(List);
export const LockIcon = adaptar(Lock);
export const LockOutlinedIcon = adaptar(Lock);
export const LoginIcon = adaptar(LogIn);
export const LogoutIcon = adaptar(LogOut);
export const MailOutlineIcon = adaptar(Mail);
export const MemoryIcon = adaptar(MemoryStick);
export const MenuIcon = adaptar(Menu);
export const MenuBookIcon = adaptar(BookOpen);
export const MicIcon = adaptar(Mic);
export const MicOffIcon = adaptar(MicOff);
export const MonitorIcon = adaptar(Monitor);
export const MonitorHeartIcon = adaptar(Activity);
export const MoreVertIcon = adaptar(EllipsisVertical);
export const MusicNoteIcon = adaptar(Music);
export const NavigateNextIcon = adaptar(ChevronRight);
export const NoteAddIcon = adaptar(FilePlus);
export const NotificationAddIcon = adaptar(BellPlus);
export const NotificationImportantIcon = adaptar(BellRing);
export const NotificationsIcon = adaptar(Bell);
export const NotificationsNoneIcon = adaptar(Bell);
export const OpenInFullIcon = adaptar(Maximize2);
export const OpenInNewIcon = adaptar(ExternalLink);
export const PaletteIcon = adaptar(Palette);
export const PanToolIcon = adaptar(Hand);
export const PatternIcon = adaptar(Grid3x3);
export const PauseIcon = adaptar(Pause);
export const PauseCircleIcon = adaptar(CirclePause);
export const PaymentsIcon = adaptar(CreditCard);
export const PendingActionsIcon = adaptar(ClipboardClock);
export const PeopleIcon = adaptar(Users);
export const PersonIcon = adaptar(User);
export const PersonOutlineIcon = adaptar(User);
export const PhotoCameraIcon = adaptar(Camera);
export const PieChartIcon = adaptar(ChartPie);
export const PlayArrowIcon = adaptar(Play);
export const PlayCircleIcon = adaptar(CirclePlay);
export const PowerSettingsNewIcon = adaptar(Power);
export const PrecisionManufacturingIcon = adaptar(Bot);
export const PrintIcon = adaptar(Printer);
export const PsychologyIcon = adaptar(Brain);
export const PublicIcon = adaptar(Globe);
export const QuestionAnswerIcon = adaptar(MessagesSquare);
export const RadarIcon = adaptar(Radar);
export const RadioButtonCheckedIcon = adaptar(CircleDot);
export const RadioButtonUncheckedIcon = adaptar(Circle);
export const RateReviewIcon = adaptar(MessageSquareText);
export const ReceiptIcon = adaptar(Receipt);
export const ReceiptLongIcon = adaptar(ReceiptText);
export const RefreshIcon = adaptar(RefreshCw);
export const RemoveCircleOutlineIcon = adaptar(CircleMinus);
export const ReplayIcon = adaptar(RotateCcw);
export const ReportProblemIcon = adaptar(TriangleAlert);
export const RestartAltIcon = adaptar(RotateCcw);
export const RestoreFromTrashIcon = adaptar(ArchiveRestore);
export const RuleIcon = adaptar(ListChecks);
export const SaveIcon = adaptar(Save);
export const ScheduleIcon = adaptar(Clock);
export const ScienceIcon = adaptar(FlaskConical);
export const SearchIcon = adaptar(Search);
export const SecurityIcon = adaptar(ShieldCheck);
export const SendIcon = adaptar(Send);
export const SettingsIcon = adaptar(Settings);
export const SettingsSuggestIcon = adaptar(Settings2);
export const ShoppingCartIcon = adaptar(ShoppingCart);
export const ShowChartIcon = adaptar(ChartLine);
export const SignalCellularAltIcon = adaptar(SignalHigh);
export const SignalWifiStatusbarConnectedNoInternet4Icon = adaptar(WifiOff);
export const SmartToyIcon = adaptar(Bot);
export const SpeedIcon = adaptar(Gauge);
export const StopIcon = adaptar(Square);
export const StopCircleIcon = adaptar(CircleStop);
export const StorageIcon = adaptar(Database);
export const StorefrontIcon = adaptar(Store);
export const SubdirectoryArrowRightIcon = adaptar(CornerDownRight);
export const SupportAgentIcon = adaptar(Headset);
export const SyncLockIcon = adaptar(RotateCcwKey);
export const TableChartIcon = adaptar(Table);
export const TaskAltIcon = adaptar(CircleCheckBig);
export const ThumbDownIcon = adaptar(ThumbsDown);
export const ThumbDownOutlinedIcon = adaptar(ThumbsDown);
export const ThumbUpIcon = adaptar(ThumbsUp);
export const ThumbUpOutlinedIcon = adaptar(ThumbsUp);
export const TimerOffIcon = adaptar(TimerOff);
export const TokenIcon = adaptar(CircleDollarSign);
export const TollIcon = adaptar(Coins);
export const TravelExploreIcon = adaptar(Globe);
export const TrendingUpIcon = adaptar(TrendingUp);
export const TuneIcon = adaptar(SlidersHorizontal);
export const UploadIcon = adaptar(Upload);
export const UploadFileIcon = adaptar(FileUp);
export const VerifiedIcon = adaptar(BadgeCheck);
export const ViewColumnIcon = adaptar(Columns3);
export const ViewQuiltIcon = adaptar(PanelsTopLeft);
export const ViewSidebarIcon = adaptar(PanelRight);
export const VisibilityIcon = adaptar(Eye);
export const VisibilityOffIcon = adaptar(EyeOff);
export const VpnKeyIcon = adaptar(KeyRound);
export const VpnKeyOutlinedIcon = adaptar(KeyRound);
export const WarningIcon = adaptar(TriangleAlert);
export const WarningAmberIcon = adaptar(TriangleAlert);
export const WidgetsIcon = adaptar(LayoutGrid);
export const WifiIcon = adaptar(Wifi);
export const WifiOffIcon = adaptar(WifiOff);
export const WorkspacesIcon = adaptar(Group);
export const ZoomInIcon = adaptar(ZoomIn);
export const ZoomOutIcon = adaptar(ZoomOut);
